import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import moment from 'moment';

import { TextRegular, TextSemiBold } from 'UiKit/typography';
import { ChevronDownIcon, ChevronFillDownIcon, ChevronFillTopIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';

import { numHour, numMinute } from './const';
import styles from './styles.module.css';
import Cookies from 'universal-cookie';

const TimeItem = memo(({ num, idPrefix, isDisabled, isSelected, onSelect }) => {
  const id = `${idPrefix}-${num}`;

  return (
    <Box
      id={id}
      className={`${styles['time-item__container']} ${isSelected && styles['time-item__container--selected']}`}
      onClick={() => !isDisabled && onSelect(num)}
    >
      <TextRegular className={styles['time__text']} color={isDisabled ? 'var(--color-neutral50)' : 'var(--color-semantic-text)'}>
        {String(num).padStart(2, '0')}
      </TextRegular>
    </Box>
  );
});

TimeItem.displayName = 'TimeItem';

const ChevronButton = memo(({ direction, targetId, onScroll }) => {
  const Icon = direction === 'up' ? ChevronFillTopIcon : ChevronFillDownIcon;

  return (
    <Flex className={`${styles['chevron__container']} center-content`} onClick={() => onScroll(targetId)}>
      <Icon color="var(--color-semantic-text)" size="16px" />
    </Flex>
  );
});

ChevronButton.displayName = 'ChevronButton';

const SliderColumn = memo(({ sliderId, items, idPrefix, onSelect, isDisabled, isSelected }) => {
  const scrollBy = useCallback((delta) => {
    const element = document.getElementById(sliderId);
    if (!element) return;
    element.scrollBy({ top: delta, behavior: 'smooth' });
  }, [sliderId]);

  return (
    <Box className={styles['popup__item']}>
      <ChevronButton direction="up" targetId={sliderId} onScroll={scrollBy} />
      <Box className={styles['slider__container']} id={sliderId}>
        <Iteration
          items={items}
          render={(num) => (
            <TimeItem
              key={num}
              num={num}
              idPrefix={idPrefix}
              isDisabled={isDisabled(num)}
              isSelected={isSelected(num)}
              onSelect={onSelect}
            />
          )}
        />
      </Box>
      <ChevronButton direction="down" targetId={sliderId} onScroll={(id) => scrollBy(200)} />
    </Box>
  );
});

SliderColumn.displayName = 'SliderColumn';

const InputTime = ({ value = new Date(), onChange, error, placeholder, minTime: minTimeProp, maxTime: maxTimeProp, ...props }) => {
  const cookies = new Cookies();
  const timezone = cookies.get('organization')?.timezone;
  const selectedTime = value ? moment(value).clone().tz(timezone).format('HH:mm') : null;
  const minTime = minTimeProp ? moment(minTimeProp).format('HH:mm') : '00:00';
  const maxTime = maxTimeProp ? moment(maxTimeProp).format('HH:mm') : null;
  const isError = !!error && Object.keys(error).length > 0;
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [time, setTime] = useState({ hour: null, minute: null });
  const [openUp, setOpenUp] = useState(false);
  const containerRef = useRef(null);

  const onChangeHour = useCallback((value) => setTime({ ...time, hour: value }), [time]);
  const onChangeMinute = useCallback((value) => setTime({ ...time, minute: value }), [time]);
  const onClickNow = () => {
    setTime({ hour: moment().format('HH'), minute: moment().format('mm') });
    onChange(moment());
    onClose();
  };

  const onSave = () => {
    const timeValue = moment.tz(`${time.hour}:${time.minute}`, 'HH:mm', timezone);
    onChange(timeValue);
    onClose();
  };

  const handleOpen = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUp(spaceBelow < 303 + 8);
    }
    onOpen();
  };

  const checkIsDisableHour = (num) => {
    const minHour = Number(minTime.split(':')[0]);
    if (minHour > Number(num)) return true;

    if (maxTime) {
      const maxHour = Number(maxTime.split(':')[0]);
      if (maxHour < Number(num)) return true;
    }

    return false;
  };

  const checkIsDisableMinute = (num) => {
    const [minHour, minMinute] = minTime.split(':');

    if (Number(time?.hour) <= Number(minHour) && Number(minMinute) > Number(num)) return true;

    if (maxTime) {
      const [maxHour, maxMinute] = maxTime.split(':');
      if (Number(time?.hour) > Number(maxHour)) return true;
      if (Number(time?.hour) === Number(maxHour) && Number(num) > Number(maxMinute)) return true;
    }

    return false;
  };

  useEffect(() => {
    if (selectedTime) {
      const [hour, minute] = selectedTime?.split(':');
      setTime({ hour, minute });
    }
  }, [selectedTime]);

  return (
    <Box {...props}>
      <Flex ref={containerRef} className={styles['input-time__container']}>
        <Flex
          className={styles['selection__container']}
          onClick={handleOpen}
          bg={selectedTime ? 'var(--color-semantic-background)' : 'white'}
          borderColor={isError ? 'var(--color-error50)' : undefined}
        >
          <TextRegular color={selectedTime ? undefined : 'var(--color-neutral50)'}>{selectedTime || placeholder}</TextRegular>
          <ChevronDownIcon size="20px" color="var(--color-semantic-text)" />
        </Flex>

        <Condition statement={isOpen}>
          <OutsideClickHandler onOutsideClick={onClose}>
            <Box className={styles['popup__container']} style={openUp ? { top: 'auto', bottom: '48px' } : { top: '48px', bottom: 'auto' }}>
              <Flex className={styles['popup__content']}>
                <SliderColumn
                  sliderId="slider__container--hour"
                  items={numHour}
                  idPrefix="time-item__container--hour"
                  onSelect={onChangeHour}
                  isDisabled={checkIsDisableHour}
                  isSelected={(num) => Number(time?.hour) === Number(num)}
                />

                <SliderColumn
                  sliderId="slider__container--minute"
                  items={numMinute}
                  idPrefix="time-item__container--minute"
                  onSelect={onChangeMinute}
                  isDisabled={checkIsDisableMinute}
                  isSelected={(num) => time?.minute === num}
                />
              </Flex>

              <Flex className={styles['popup__footer']}>
                <TextSemiBold className={styles['now__button']} onClick={onClickNow}>
                  Now
                </TextSemiBold>

                <TextSemiBold className={styles['save__button']} onClick={onSave}>
                  Save
                </TextSemiBold>
              </Flex>
            </Box>
          </OutsideClickHandler>
        </Condition>
      </Flex>
      <Condition statement={isError}>
        <TextRegular color="var(--color-error50)" fontSize="var(--font-caption)" marginTop="4px">
          {error?.message}
        </TextRegular>
      </Condition>
    </Box>
  );
};

export default InputTime;
